import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, OnInit, TemplateRef, viewChild } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  MatLegacyTableDataSource as MatTableDataSource,
  MatLegacyTableModule as MatTableModule,
} from '@angular/material/legacy-table';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TsButtonModule, TsSnackbarService } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { TsTabDefinition, TsTabGroupComponent } from 'tsui/lib/ts-tab-group/ts-tab-group.component';
import { Actions, Store, ofActionCompleted, ofActionErrored } from '@ngxs/store';
import { Folders } from '../state/actions';
import { AccountState } from 'app/state/account/state';
import { filter, tap, switchMap } from 'rxjs';
import { Account } from 'app/core/models/account.types';
import { FileResource, Document } from '../folders.types';
import { DELETE_LAST_DOCUMENT_CONFIRMATION } from '../folders.constants';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { WidgetsModule } from 'app/shared/components/widgets/widgets.module';
import { PipesModule } from 'app/shared/pipes/pipes.module';

export interface FoldersTabViewFileDialogData {
  fileResource: FileResource;
  readonly?: boolean;
}

@Component({
  selector: 'folders-tab-view-file',
  standalone: true,
  imports: [
    CommonModule,
    TsButtonModule,
    TsDialogComponent,
    MatIconModule,
    MatTableModule,
    TsTabGroupComponent,
    PipesModule,
    WidgetsModule,
  ],
  templateUrl: './view-file.component.html',
  styleUrls: ['./view-file.component.scss'],
})
export class FoldersTabViewFileComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  static getDialogConfig(data: FoldersTabViewFileDialogData) {
    return {
      ...TsDialogComponent.getDefaultDialogConfig(),
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
      data,
    };
  }

  private readonly data = inject<FoldersTabViewFileDialogData>(DIALOG_DATA);
  private readonly dialogRef = inject(DialogRef);

  localDocsTemplate = viewChild<TemplateRef<unknown>>('localDocsTemplate');
  integrationDocsTemplate = viewChild<TemplateRef<unknown>>('integrationDocsTemplate');

  editWopiFile = false;
  expandRightSidebar = true;
  selectedFile: FileResource = this.data.fileResource;
  selectedDocument: Document;
  localDocumentsDataSource = new MatTableDataSource<Document>();
  integrationDocumentsDataSource = new MatTableDataSource<Document>();
  localDocumentsColumns = ['version', 'assetable_category_label', 'created_by', 'created_at', 'actions'];
  integrationDocumentsColumns = ['version', 'integrated_storage', 'created_by', 'created_at', 'actions'];
  tabs: TsTabDefinition[];
  account: Account;

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private store: Store,
    private readonly actions$: Actions,
    private confirmService: ConfirmService,
    private readonly snackBar: TsSnackbarService,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
  }

  get isReadonly(): boolean {
    return !!this.data?.readonly;
  }

  ngOnInit(): void {
    this.loadFile();
    this.setupStateActions();
  }

  ngAfterViewInit(): void {
    this.tabs = [
      { label: 'Documents', template: this.localDocsTemplate() },
      { label: 'Integration Documents', template: this.integrationDocsTemplate() },
    ];
    this._changeDetectorRef.detectChanges();
  }

  setSelectedDocument(document?: Document): void {
    if (document) {
      this.selectedDocument = document;
    } else if (this.selectedFile.documents?.length > 0) {
      this.selectedDocument = this.selectedFile.documents[0];
    }
  }

  toggleWopiFile(): void {
    this.editWopiFile = !this.editWopiFile;

    if (this.selectedFile?.wopi?.errors) {
      return;
    }

    if (this.editWopiFile) {
      this._changeDetectorRef.detectChanges();
      // TODO: This is probably not the best way to accomplish posting to the form.
      const form = window.document.getElementById('officeForm') as HTMLFormElement;

      if (form) {
        form.submit();
      }
    }
  }

  downloadDocument(document: Document): void {
    window.open(document.original_url, '_blank');
  }

  deleteDocument(document: Document): void {
    if (this.isReadonly) {
      return;
    }

    const isLastDocument = (this.selectedFile.documents?.length ?? 0) <= 1;
    this.confirmService
      .openConfirmationModal(isLastDocument ? DELETE_LAST_DOCUMENT_CONFIRMATION : undefined)
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        switchMap(() =>
          isLastDocument
            ? this.store.dispatch(new Folders.DeleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id))
            : this.store.dispatch(
                new Folders.DeleteDocument(this.selectedFile.folder_resource_id, this.selectedFile.id, document.id),
              ),
        ),
      )
      .subscribe(() => {
        if (isLastDocument) {
          this.discard();
        } else {
          this.loadFile();
        }
      });
  }

  deleteFile(): void {
    if (this.isReadonly) {
      return;
    }

    this.confirmService
      .openConfirmationModal()
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        switchMap(() =>
          this.store.dispatch(new Folders.DeleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id)),
        ),
      )
      .subscribe(() => {
        this.discard();
      });
  }

  onFileChanged(event: any) {
    if (this.isReadonly) {
      return;
    }

    const document: File = event.target.files[0];
    this.store
      .dispatch(new Folders.CreateFileDocument(this.selectedFile.folder_resource_id, this.selectedFile.id, document))
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        event.target.value = '';
        this.loadFile();
      });
  }

  openOnline(document) {
    window.open(document?.links?.edit?.link?.webUrl, '_blank');
  }

  discard(): void {
    this.dialogRef.close();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  private setupStateActions(): void {
    // Fallback to when GetFileById endpoint fails.
    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionErrored(Folders.GetFileById),
        tap(() => {
          this.snackBar.open('Unable to open the file. Please try again later.', 'error');
          this.discard();
        }),
      )
      .subscribe();

    this.actions$
      .pipe(untilComponentDestroyed(this), ofActionCompleted(Folders.GetFileById))
      .subscribe(() => this.store.dispatch(new Folders.GetFolder(this.selectedFile.folder_resource_id)));
  }

  private loadFile(): void {
    this.store
      .dispatch(new Folders.GetFileById(this.selectedFile.id))
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        const fileResource: FileResource = this.store.selectSnapshot((state) => state.folderState.selectedFile);
        if (fileResource) {
          this.localDocumentsDataSource.data = (fileResource.local_documents ?? []).sort((a, b) =>
            a.id < b.id ? 1 : -1,
          );
          this.integrationDocumentsDataSource.data = (fileResource.integration_documents ?? []).sort((a, b) =>
            a.id < b.id ? 1 : -1,
          );
          this.selectedFile = fileResource;
          this.setSelectedDocument();
        }

        this._changeDetectorRef.markForCheck();
      });
  }
}
